import { type Mode, MODES } from "#src/constants/mode.ts";
import { type ProductBase } from "#src/types/products/ProductBase.ts";
import { type ProductType, PRODUCT_TYPE } from "#src/constants/products.ts";

import {
  insuredQuoteData,
  parseBoolean,
  parseType,
  type ParsingData,
  type ModeDataItem,
  type QuoteRequestParams,
  type RenderRules,
} from "#src/types/QuoteParams.ts";

export interface ReturnOfPremiumProductType extends ProductBase {
  isRopTerm: boolean;
  termLengths: number[];
  requestCategoryGenerator: (termLength: number | null) => string[];
  defaults: {
    active_years: number;
    save_age: boolean;
  };
  eAppParser(
    args: Partial<ParsingData>
  ): ReturnOfPremiumProductType["defaults"] & {
    face_amount: number;
    mode: Mode;
  };
}

function showHealth(conversion: boolean) {
  return !conversion;
}

export function ReturnOfPremium(): ReturnOfPremiumProductType {
  return {
    renderRules(
      _quoteParams,
      options = { conversion: false }
    ): Partial<RenderRules> {
      return {
        saveAge: true,
        termLength: true,
        faceAmount: true,
        insuredHealth: showHealth(options.conversion),
        mode: true,
        state: true,
      };
    },
    get isRopTerm(): boolean {
      return true;
    },
    get type(): ProductType {
      return PRODUCT_TYPE.ROP_TERM;
    },
    get display(): string {
      return "Return of Premium Term";
    },
    get requestCategory(): string {
      return "term.return_of_premium";
    },
    get termLengths(): number[] {
      return [30, 20];
    },
    get defaults() {
      return {
        active_years: 20,
        save_age: false,
      };
    },
    get modes(): ModeDataItem[] {
      return MODES;
    },
    //When is this used?
    requestCategoryGenerator(termLength = null): string[] {
      const reqCatGen = (cat: string, len: number | null) =>
        len ? `${cat}.${len}_year` : cat;
      return [reqCatGen(this.requestCategory, termLength)];
    },
    eAppParser(model): ReturnType<ReturnOfPremiumProductType["eAppParser"]> {
      return {
        active_years: parseType<number>(
          model.active_years,
          this.termLengths,
          this.defaults.active_years
        ),
        save_age: parseBoolean(model.save_age, this.defaults.save_age),
        mode: model.mode as Mode,
        face_amount: model?.face_amount || 1000000,
      };
    },
    toQuoteRequest(args): Partial<QuoteRequestParams> {
      return {
        agent: { id: args.agentId },
        compare: false,

        ...insuredQuoteData(args.insured),

        selected_type: args.params.selected_type,
        state: args.params.state,
        categories: this.requestCategoryGenerator(
          args.params.active_years || null
        ),

        mode: args.params.mode,
        save_age: args.params.save_age,
        face_amount: args.params.face_amount,
      };
    },
  };
}
